import { estimateAnalysisRange, replaceBillingTranslationValues } from './billingPricing';

export type LandingPricingPlan = 'free' | 'pro' | 'enterprise';
export type LandingPricingLanguage = 'en' | 'fr';

export const LANDING_MONTHLY_CONSULTANT_CREDITS = 600_000;
export const LANDING_CONSULTANT_ANALYSIS_RANGE = estimateAnalysisRange(LANDING_MONTHLY_CONSULTANT_CREDITS)!;

export interface LandingPricingAnalysisEstimate {
  monthlyCredits: number;
  range: { min: number; max: number };
  tooltipKey: 'agentEstimateTooltip' | 'transportEstimateTooltip';
}

const CONSULTANT_ANALYSIS_ESTIMATE: LandingPricingAnalysisEstimate = {
  monthlyCredits: LANDING_MONTHLY_CONSULTANT_CREDITS,
  range: LANDING_CONSULTANT_ANALYSIS_RANGE,
  tooltipKey: 'agentEstimateTooltip',
};

const LANDING_PRICING_FEATURES = [
  { key: 'currentRegulations' },
  { key: 'monthlyCredits' },
  { key: 'adrAgent', tooltip: 'adrAgentTooltip', analysisEstimate: CONSULTANT_ANALYSIS_ESTIMATE },
  { key: 'directSourceConsultation', paidOnly: true },
  { key: 'transportAgent', tooltip: 'transportAgentTooltip', enterpriseOnly: true },
  { key: 'apiAccess', tooltip: 'apiAccessTooltip', enterpriseOnly: true },
  { key: 'support', paidOnly: true },
  { key: 'customization', label: 'enterprise.customization', tooltip: 'enterprise.customizationTooltip', enterpriseOnly: true },
  { key: 'affiliateProgram', tooltip: 'affiliateProgramTooltip', paidOnly: true },
  { key: 'supportFrenchTech', paidOnly: true },
] as const;

export function getLandingPricingPlanTitleKey(plan: LandingPricingPlan) {
  return plan === 'free' ? 'freeTitle' : plan === 'pro' ? 'proTitle' : 'enterprise.title';
}

export function getLandingPricingFeatures(plan: LandingPricingPlan, language: LandingPricingLanguage, freeCredits?: number) {
  const freeQuota = typeof freeCredits === 'number' && Number.isFinite(freeCredits) && freeCredits >= 0 ? freeCredits : undefined;
  return LANDING_PRICING_FEATURES
    .filter((feature) => feature.key !== 'supportFrenchTech' || language === 'fr')
    .map((feature) => {
      const included = plan === 'enterprise' || (!('enterpriseOnly' in feature) && !(plan === 'free' && 'paidOnly' in feature));
      return {
        key: feature.key,
        included,
        labelKey: feature.key === 'monthlyCredits' && plan === 'enterprise'
          ? 'unlimitedCredits'
          : feature.key === 'monthlyCredits' && plan === 'free' && freeQuota === undefined
          ? 'monthlyCreditsPending'
          : feature.key === 'support' && plan === 'enterprise'
          ? 'enterprise.prioritySupport'
          : 'label' in feature ? feature.label : feature.key,
        ...('tooltip' in feature ? { tooltip: feature.tooltip } : {}),
        unlimitedUsage: plan === 'enterprise' && (feature.key === 'adrAgent' || feature.key === 'transportAgent'),
        ...(feature.key === 'monthlyCredits' && plan !== 'enterprise' && (plan !== 'free' || freeQuota !== undefined) ? {
          labelValues: {
            credits: plan === 'free' ? freeQuota! : LANDING_MONTHLY_CONSULTANT_CREDITS,
          },
        } : {}),
        ...('analysisEstimate' in feature && included && plan !== 'enterprise' && (plan !== 'free' || freeQuota !== undefined) ? {
          analysisEstimate: plan === 'free' && feature.key === 'adrAgent' ? {
            monthlyCredits: freeQuota!,
            range: freeQuota === 0 ? { min: 0, max: 0 } : { min: 1, max: 3 },
            tooltipKey: 'agentEstimateTooltip' as const,
          } : feature.analysisEstimate,
        } : {}),
      };
    });
}

export function formatLandingPricingFeatureLabel(
  feature: ReturnType<typeof getLandingPricingFeatures>[number], label: string, language: LandingPricingLanguage,
) {
  const formatter = new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : 'en-US');
  return replaceBillingTranslationValues(label, Object.fromEntries(
    Object.entries(feature.labelValues || {}).map(([key, value]) => [key, formatter.format(value)]),
  ));
}
